import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const RuleOf72Calculator = () => {
  const [formData, setFormData] = useState({
    interestRate: '',
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateYearsToDouble = () => {
    const { interestRate } = formData;

    if (!interestRate) {
      setError('Please fill in the interest rate field');
      return;
    }

    const rate = parseFloat(interestRate);

    if (isNaN(rate) || rate <= 0) {
      setError('Please enter a valid number for interest rate');
      return;
    }

    const yearsToDouble = 72 / rate;

    setResults({
      interestRate: `${rate}%`,
      yearsToDouble: yearsToDouble.toFixed(2)
    });

    setChartData({
      labels: ['Interest Rate', 'Years to Double'],
      datasets: [{
        label: 'Rule of 72 Calculation',
        data: [rate, yearsToDouble],
        backgroundColor: ['rgba(75, 192, 192, 0.2)', 'rgba(255, 99, 132, 0.2)'],
        borderColor: ['rgb(75, 192, 192)', 'rgb(255, 99, 132)'],
        borderWidth: 1
      }]
    });
  };

  return (
    <div>
      <Helmet>
        <title>Rule of 72 Calculator | Financial Tool</title>
        <meta name="description" content="Calculate the number of years required to double your investment using the Rule of 72." />
        <meta name="keywords" content="Rule of 72, financial calculator, investment, doubling time" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourdomain.com/rule-of-72-calculator" />
        <meta property="og:title" content="Rule of 72 Calculator" />
        <meta property="og:description" content="Calculate the number of years required to double your investment using the Rule of 72." />
        <meta property="og:image" content="https://yourdomain.com/preview.jpg" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourdomain.com/rule-of-72-calculator" />
        <meta property="twitter:title" content="Rule of 72 Calculator" />
        <meta property="twitter:description" content="Calculate the number of years required to double your investment using the Rule of 72." />
        <meta property="twitter:image" content="https://yourdomain.com/preview.jpg" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center space-y-4 mb-8">
            <div className="text-4xl">💰</div>
            <h1 className="text-3xl font-bold text-gray-800">Rule of 72 Calculator</h1>
            <p className="text-gray-600">
              Calculate the number of years required to double your investment using the Rule of 72.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white/80 backdrop-blur-lg rounded-xl shadow-lg p-6 md:p-8"
          >
            <div className="space-y-6">
              <div>
                <label htmlFor="interestRate" className="block text-sm font-medium text-gray-700 mb-2">
                  Interest Rate (%)
                </label>
                <input
                  type="number"
                  id="interestRate"
                  name="interestRate"
                  value={formData.interestRate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Enter interest rate"
                />
              </div>

              <button
                onClick={calculateYearsToDouble}
                className="w-full py-3 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors duration-200"
              >
                Calculate Years to Double
              </button>

              {error && (
                <div className="text-red-500 text-center mt-4">
                  {error}
                </div>
              )}

              {results && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Calculation Results</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-xl shadow-md border">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Interest Rate</h3>
                      <p className="text-lg font-semibold">{results.interestRate}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-md border">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Years to Double</h3>
                      <p className="text-lg font-semibold">{results.yearsToDouble}</p>
                    </div>
                  </div>

                  {chartData && (
                    <div className="w-full max-w-lg mx-auto">
                      <h3 className="text-xl font-semibold mb-4">Visualization</h3>
                      <div className="w-full h-64">
                        <Bar
                          data={chartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                              y: {
                                beginAtZero: true,
                                title: {
                                  display: true,
                                  text: 'Values'
                                }
                              },
                              x: {
                                title: {
                                  display: true,
                                  text: 'Parameters'
                                }
                              }
                            },
                            plugins: {
                              title: {
                                display: true,
                                text: 'Rule of 72 Calculation'
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          <div className="calculator-section mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              How to Use Rule of 72 Calculator <span className="ml-2 text-2xl">📋</span>
            </h2>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <p className="text-gray-700 mb-6">
                Follow these steps to use the Rule of 72 Calculator effectively:
              </p>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Interest Rate</h3>
                  <ul className="list-disc pl-8 space-y-2">
                    <li>Enter the expected annual yield of your investment, e.g., 8%</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Calculate</h3>
                  <ul className="list-disc pl-8 space-y-2">
                    <li>Click the "Calculate Years to Double" button</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Review Results</h3>
                  <ul className="list-disc pl-8 space-y-2">
                    <li>Review the calculated number of years to double your investment</li>
                    <li>Use the graph to visualize the relationship between interest rate and doubling time</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="calculator-section mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              Features and Benefits <span className="ml-2 text-2xl">✨</span>
            </h2>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <ul className="list-disc pl-8 space-y-4">
                <li className="flex items-center">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
                  <span className="font-medium">Quick Calculation:</span> Instantly calculate the years to double your investment.
                </li>
                <li className="flex items-center">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
                  <span className="font-medium">Visualization:</span> Graphical representation of the Rule of 72 calculation.
                </li>
                <li className="flex items-center">
                  <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
                  <span className="font-medium">User-Friendly:</span> Simple and intuitive interface for easy use.
                </li>
                <li className="flex items-center">
                  <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
                  <span className="font-medium">Educational:</span> Helps users understand the concept of compound interest and investment growth.
                </li>
              </ul>
            </div>
          </div>

          <div className="calculator-section mt-24">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              Frequently Asked Questions (FAQ) <span className="ml-2 text-2xl">❓</span>
            </h2>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold text-blue-600 mb-4">What is the Rule of 72?</h3>
                  <p className="text-gray-700">
                    The Rule of 72 is a simple formula used to estimate the number of years required to double the invested money at a given annual rate of return.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-blue-600 mb-4">How accurate is the Rule of 72?</h3>
                  <p className="text-gray-700">
                    The Rule of 72 provides a rough estimate and is most accurate for interest rates between 6% and 10%.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this calculator for other financial calculations?</h3>
                  <p className="text-gray-700">
                    This calculator is specifically designed for the Rule of 72. For other financial calculations, consider using specialized tools.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-blue-600 mb-4">Is this calculator free to use?</h3>
                  <p className="text-gray-700">
                    Yes, this calculator is completely free to use.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuleOf72Calculator;